import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Heart, CalendarDays, Disc3, Newspaper } from "lucide-react";
import { api } from "../lib/api";
import DoorCard from "../components/DoorCard";
import MediaFrame from "../components/MediaFrame";
import Marquee from "../components/Marquee";
import Starfield from "../components/Starfield";
import Reveal from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import NewsletterForm from "../components/NewsletterForm";

const HERO_LINES = ["Plusieurs projets,", "un même élan,", "chacun sa trajectoire."];

const CHAPTERS = [
  { n: "01", title: "Création", text: "Une maison qui écrit, enregistre et produit — de la première note à l'objet fini." },
  { n: "02", title: "Liberté", text: "Une musique affranchie des formats, qui respire et n'obéit qu'à elle-même." },
  { n: "03", title: "Reliance", text: "Relier celles et ceux qui créent à celles et ceux qui écoutent. C'est tout, et c'est l'essentiel." },
];

function formatDate(d) {
  return new Date(d + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

export default function Home() {
  const [data, setData] = useState(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => {
    api.get("/home").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const artistName = (id) => data?.artists?.find((a) => a.id === id)?.name || "Joy Messenger";

  return (
    <main data-testid="home-page">
      {/* ---------- HERO ---------- */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden">
        <motion.div style={{ y: yBg }} className="absolute inset-[-10%] jm-drift">
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(90% 70% at 15% 20%, rgba(217,119,6,0.16), transparent 55%), radial-gradient(70% 60% at 85% 75%, rgba(197,160,89,0.12), transparent 55%), radial-gradient(50% 40% at 60% 15%, rgba(234,179,8,0.07), transparent 60%), linear-gradient(180deg, #10121a 0%, #0d0e12 100%)",
            }}
          />
        </motion.div>
        <Starfield />
        <div className="absolute inset-0 jm-grain opacity-50 pointer-events-none" />
        <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-t from-jm-ink to-transparent" />

        <motion.div style={{ opacity: fade }} className="jm-container relative pt-32 pb-24 w-full">
          <motion.p
            data-testid="hero-overline"
            className="jm-overline mb-8"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Des trajectoires singulières, un espace en commun
          </motion.p>
          <h1 className="font-light text-jm-bone text-[13vw] sm:text-6xl lg:text-7xl xl:text-[5.2rem] leading-[1.05] max-w-5xl" style={{ fontFamily: "'Boska', serif", letterSpacing: "0.03em" }}>
            {HERO_LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "italic font-normal text-jm-brass" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.35 + i * 0.16, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            className="mt-8 max-w-xl text-base sm:text-lg text-jm-smoke leading-relaxed"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.9 }}
          >
            Entre jazz, improvisation et musiques parallèles, Joy Messenger accompagne des créations
            authentiques, affranchies des formats — des musiques qui respirent et relient celles et ceux qui
            les créent à celles et ceux qui les écoutent.
          </motion.p>
          <motion.div
            className="mt-10 flex flex-wrap items-center gap-4"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
          >
            <a
              href="#portes"
              data-testid="hero-discover-button"
              className="inline-flex items-center gap-2 bg-jm-bone text-jm-ink px-7 py-3.5 text-sm font-medium tracking-wide hover:bg-jm-amber transition-colors duration-300"
            >
              Découvrir les projets
              <ArrowDown size={15} />
            </a>
            <Link
              to="/soutenir"
              data-testid="hero-support-link"
              className="inline-flex items-center gap-2 border border-jm-brass/40 text-jm-bone px-7 py-3.5 text-sm tracking-wide hover:border-jm-amber hover:text-jm-amber transition-colors duration-300"
            >
              <Heart size={14} />
              Soutenir la création
            </Link>
          </motion.div>
        </motion.div>

        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 text-jm-mute"
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
        >
          <span className="font-mono text-[9px] uppercase tracking-[0.3em]">Défiler</span>
          <span className="w-px h-8 bg-gradient-to-b from-jm-brass/60 to-transparent" />
        </motion.div>
      </section>

      {/* ---------- MARQUEE ---------- */}
      <Marquee items={["SIRIN CODAL", "NICODRI TRIO", "WIB ORGANIC TRIO", "BAM THE HOWLS", "SMALLCO", "JOY MESSENGER"]} />

      {/* ---------- LES PORTES ---------- */}
      <section id="portes" className="jm-container py-24 sm:py-32">
        <SectionHeading
          overline="Les portes"
          title="Des univers, des trajectoires singulières"
          linkTo="/artistes"
          linkLabel="Tous les projets"
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" data-testid="artist-portals">
          {(data?.artists || []).map((a, i) => (
            <DoorCard key={a.id} artist={a} index={i} />
          ))}
        </div>
      </section>

      {/* ---------- MANIFESTO ---------- */}
      <section className="border-y border-jm-brass/15 bg-jm-coal/40">
        <div className="jm-container py-24 sm:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <p className="jm-overline mb-5">Notre philosophie</p>
            <p className="font-serif font-light text-3xl sm:text-4xl lg:text-[2.9rem] leading-[1.15] text-jm-bone">
              Faire vivre une musique <em className="text-jm-brass not-italic font-serif italic">libre</em>,
              authentique, qui <em className="font-serif italic text-jm-brass">respire</em>.
            </p>
          </Reveal>
          <div className="lg:col-span-7 flex flex-col justify-center divide-y divide-jm-brass/10">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={0.1 * i}>
                <div className="flex items-baseline gap-6 sm:gap-10 py-7 group" data-testid={`manifesto-chapter-${c.n}`}>
                  <span className="font-mono text-xs text-jm-amber tracking-[0.2em]">{c.n}</span>
                  <div>
                    <h3 className="font-serif text-2xl sm:text-3xl text-jm-bone group-hover:text-jm-brass transition-colors duration-500">
                      {c.title}
                    </h3>
                    <p className="mt-2 text-sm sm:text-base text-jm-smoke leading-relaxed max-w-lg">{c.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- ACTUALITÉS ---------- */}
      {data?.news?.length > 0 && (
        <section className="jm-container py-24 sm:py-32">
          <SectionHeading overline="Le journal" title="Dernières actualités" linkTo="/actualites" linkLabel="Toutes les actualités" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {data.news.map((n, i) => (
              <Reveal key={n.id} delay={0.08 * i}>
                <Link
                  to="/actualites"
                  data-testid={`home-news-${n.id}`}
                  className="group block h-full border border-jm-brass/15 hover:border-jm-brass/40 bg-jm-card/60 p-7 transition-colors duration-500"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <Newspaper size={14} className="text-jm-amber" />
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-jm-mute">{formatDate(n.date)}</span>
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl text-jm-bone leading-snug group-hover:text-jm-brass transition-colors duration-300">
                    {n.title}
                  </h3>
                  <p className="mt-4 text-sm text-jm-smoke leading-relaxed">{n.excerpt}</p>
                  <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.22em] text-jm-brass/80">{artistName(n.artist)}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* ---------- SORTIES ---------- */}
      {data?.releases?.length > 0 && (
        <section className="border-y border-jm-brass/15 bg-jm-coal/40">
          <div className="jm-container py-24 sm:py-32">
            <SectionHeading overline="Dernières sorties" title="La musique, d'abord" linkTo="/editions" linkLabel="Les Créations Joy Messenger" />
            <div className="divide-y divide-jm-brass/10 border-y border-jm-brass/10">
              {data.releases.map((r, i) => (
                <Reveal key={r.id} delay={0.06 * i}>
                  <Link
                    to="/editions"
                    data-testid={`home-release-${r.id}`}
                    className="group grid grid-cols-[auto_1fr_auto] sm:grid-cols-[110px_1fr_auto_auto] items-center gap-4 sm:gap-8 py-6 sm:py-7"
                  >
                    <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-jm-amber">
                      <Disc3 size={14} />
                      {r.type}
                    </span>
                    <div>
                      <h3 className="font-serif text-xl sm:text-2xl text-jm-bone group-hover:text-jm-brass transition-colors duration-300">
                        {r.title}
                      </h3>
                      <p className="text-[13px] text-jm-mute mt-1">{artistName(r.artist)}</p>
                    </div>
                    <span className="hidden sm:block font-mono text-xs text-jm-mute">{r.year}</span>
                    <ArrowUpRight size={17} className="text-jm-mute group-hover:text-jm-amber transition-all duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------- CONCERTS ---------- */}
      {data?.concerts?.length > 0 && (
        <section className="jm-container py-24 sm:py-32">
          <SectionHeading overline="Sur scène" title="Prochains concerts" linkTo="/concerts" linkLabel="Toutes les dates" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10">
            {data.concerts.map((c, i) => {
              const d = new Date(c.date + "T12:00:00");
              return (
                <Reveal key={c.id} delay={0.06 * (i % 2)}>
                  <Link
                    to="/concerts"
                    data-testid={`home-concert-${c.id}`}
                    className="group flex items-center gap-6 py-6 border-b border-jm-brass/10"
                  >
                    <div className="shrink-0 w-16 text-center">
                      <p className="font-serif text-3xl text-jm-bone leading-none">{d.getDate()}</p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-jm-brass mt-1.5">
                        {d.toLocaleDateString("fr-FR", { month: "short" }).replace(".", "")}
                      </p>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-jm-amber">{artistName(c.artist)}</p>
                      <p className="mt-1 text-jm-bone truncate group-hover:text-jm-brass transition-colors duration-300">
                        {c.venue} — {c.city}
                      </p>
                    </div>
                    <CalendarDays size={17} className="shrink-0 text-jm-mute group-hover:text-jm-amber transition-colors" />
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </section>
      )}

      {/* ---------- BOUTIQUE ---------- */}
      {data?.products?.length > 0 && (
        <section className="border-t border-jm-brass/15 bg-jm-coal/40">
          <div className="jm-container py-24 sm:py-32">
            <SectionHeading overline="La boutique" title="Objets & éditions" linkTo="/boutique" linkLabel="Voir la boutique" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
              {data.products.map((p, i) => (
                <Reveal key={p.id} delay={0.07 * i}>
                  <Link to={`/boutique/${p.id}`} data-testid={`home-product-${p.id}`} className="group block">
                    <div className="relative overflow-hidden border border-jm-brass/15 group-hover:border-jm-brass/40 transition-colors duration-500">
                      <div className="aspect-square transition-transform duration-[1100ms] ease-out group-hover:scale-[1.04]">
                        <MediaFrame label={p.category} sublabel="Joy Messenger" accent="#C5A059" image={p.image} className="w-full h-full" />
                      </div>
                      {p.badge && (
                        <span className="absolute top-3 left-3 bg-jm-amber text-jm-ink font-mono text-[9px] uppercase tracking-[0.18em] px-2.5 py-1">
                          {p.badge}
                        </span>
                      )}
                    </div>
                    <div className="pt-4 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-serif text-base sm:text-lg text-jm-bone leading-snug group-hover:text-jm-brass transition-colors duration-300">
                          {p.name}
                        </h3>
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-jm-mute mt-1.5">{artistName(p.artist)}</p>
                      </div>
                      <span className="shrink-0 font-mono text-sm text-jm-brass">{p.price} €</span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------- SOUTENIR ---------- */}
      <section className="relative overflow-hidden border-t border-jm-brass/15">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(80% 90% at 50% 110%, rgba(217,119,6,0.22), transparent 60%), linear-gradient(180deg, #0d0e12, #12141c)",
          }}
        />
        <div className="absolute inset-0 jm-grain opacity-40 pointer-events-none" />
        <div className="jm-container relative py-28 sm:py-36 text-center">
          <Reveal>
            <p className="jm-overline mb-6">Soutenir</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif font-light text-3xl sm:text-5xl lg:text-6xl text-jm-bone leading-[1.12] max-w-4xl mx-auto">
              Soutenir la création musicale <em className="italic text-jm-brass">indépendante</em>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-7 max-w-2xl mx-auto text-jm-smoke leading-relaxed">
              Adhésion, don, mécénat : chaque geste aide la maison à produire des albums, organiser des concerts
              et faire exister une musique libre, loin des logiques de marché.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <Link
              to="/soutenir"
              data-testid="home-support-cta"
              className="mt-10 inline-flex items-center gap-2.5 bg-jm-amber text-jm-ink px-9 py-4 text-sm font-medium tracking-wide hover:bg-jm-gold transition-colors duration-300"
            >
              <Heart size={15} />
              Devenir passeur de musique
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ---------- NEWSLETTER ---------- */}
      <section className="border-t border-jm-brass/15">
        <div className="jm-container py-24 sm:py-28 text-center">
          <Reveal>
            <p className="jm-overline mb-5">La lettre de la maison</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-jm-bone mb-4">Restez au plus près de la musique</h2>
            <p className="text-jm-smoke text-sm sm:text-base mb-10 max-w-lg mx-auto">
              Sorties, concerts, sessions et coulisses — directement dans votre boîte.
            </p>
            <NewsletterForm />
          </Reveal>
        </div>
      </section>
    </main>
  );
}
